<script setup lang="ts">
import { api } from '@/api';
import {
  ParameterCombinationDto,
  QaEquipmentRtSystemDto,
  TaskInJobDto,
  WorkStepPreviewDto,
} from '@/api/generator';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import Textarea from '@/components/forms/Textarea/Textarea.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import NamedGroup from '@/components/NamedGroup/NamedGroup.vue';
import Notification from '@/components/notifications/Notification.vue';
import ParameterTable from '@/components/ParameterTable/ParameterTable.vue';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import TaskStepTitle from '@/components/TaskStepTitle/TaskStepTitle.vue';
import { useTaskStepTitle } from '@/components/TaskStepTitle/useTaskStepTitles';
import { truthy } from '@/utils/helper';
import { buildOdataQuery } from '@/utils/odata';
import { useReferenceMeasurementInfo } from '@/utils/useReferenceMeasurementInfo.ts';
import { mdiDragHorizontal } from '@mdi/js';
import { useTranslation } from 'i18next-vue';
import { Sortable } from 'sortablejs-vue3';
import { useFieldArray, useFieldValue } from 'vee-validate';
import { computed, ref, toRef, watch } from 'vue';
import { useSupportSystemGroups } from '@/views/jobConfigurations/utils/useSupportSystemGroups';
import { useDraggableExpandpanels } from '@/views/jobConfigurations/utils';
import JobConfigurationManualEvaluations from './JobConfigurationManualEvaluations.vue';
import Attachments from '@/components/forms/Attachments/Attachments.vue';

const { t } = useTranslation();

const props = defineProps<{
  name: string;
  targetSystemEntityId: string;
}>();

const mode = useFormMode();

const task = useFieldValue<TaskInJobDto>(toRef(() => props.name));

const { fields, move, update } = useFieldArray<WorkStepPreviewDto>(
  toRef(() => `${props.name}.workSteps`),
);

const { sortableOptions, drag, dragStart, expandedIndices, expandAll, isDragging } =
  useDraggableExpandpanels(move, mode, fields);

const { taskType } = task.value;

watch(
  () => fields.value.length,
  (newLength, oldLength) => {
    if (taskType === 'ManualTask' && newLength > 0 && newLength !== oldLength) {
      expandAll();
    }
  },
  { immediate: true },
);

function toggleActivation(index: number) {
  const combinationInfo = fields.value[index].value;
  if (combinationInfo.combination) {
    combinationInfo.combination.isEnabled = !combinationInfo.combination?.isEnabled;
    update(index, combinationInfo);
  }
}

const allSupportSystems = ref<QaEquipmentRtSystemDto[]>();

async function getQaEuipmentGroups(ids: string[]) {
  const { result } = await api.qaEquipmentApi.getQaEquipmentRtSystemList(
    buildOdataQuery({
      filter: {
        id: { in: ids },
      },
    }),
  );

  allSupportSystems.value = result?.value ?? undefined;
}

const { supportSystemGroups, numGroups } = useSupportSystemGroups(
  computed(() => task.value.supportSystemIds ?? undefined),
  computed(() => allSupportSystems.value ?? undefined),
);

const allCombinations = computed<ParameterCombinationDto[]>(() =>
  fields.value?.flatMap(({ value: workStepPreview }) => workStepPreview.combination).filter(truthy),
);

watch(
  () => task.value,
  () => {
    const allSupportSystemIds = task.value.supportSystemIds
      ?.flatMap((group) => group.items)
      .filter(truthy);

    if (allSupportSystemIds) {
      getQaEuipmentGroups(allSupportSystemIds);
    }
  },
  {
    immediate: true,
  },
);
const { getNotification } = useReferenceMeasurementInfo(t);
const { getTitle } = useTaskStepTitle(allCombinations);
</script>

<template>
  <v-row v-if="numGroups || task?.instruction?.textAreaContent">
    <v-col v-if="numGroups" cols="8">
      <FormLabel class="gutterTop mb-2" :label="$t('DosimetryAndQaEquipment')">
        <NamedGroup :groups="supportSystemGroups">
          <template #default="group">
            <v-row dense class="mt-2">
              <v-col
                v-for="supportSystem in group.items"
                :key="supportSystem.id"
                cols="12"
                lg="6"
                xxl="4"
                class="mb-4"
              >
                <RTSystemTile class="w-100 h-100" :item="supportSystem" />
              </v-col>
            </v-row>
          </template>
        </NamedGroup>
      </FormLabel>
    </v-col>

    <v-col
      v-if="task?.instruction?.textAreaContent || task?.instruction?.attachments?.length"
      cols="4"
    >
      <FormLabel :label="$t('Instruction_one')">
        <div v-if="task?.instruction?.textAreaContent">
          <Textarea :modelValue="task?.instruction?.textAreaContent" mode="details" />
        </div>
        <v-divider
          v-if="task?.instruction?.textAreaContent && task?.instruction?.attachments?.length"
          color="primary-lighten-4"
          class="mt-1 mb-1"
          thickness="1"
        ></v-divider>
        <Attachments
          v-if="task?.instruction?.attachments?.length"
          :modelValue="task?.instruction?.attachments"
          mode="details"
        />
      </FormLabel>
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-expansion-panels v-model="expandedIndices" :readonly="isDragging" multiple>
        <Sortable
          :list="fields"
          :options="sortableOptions"
          itemKey="key"
          class="w-100"
          @start="dragStart"
          @end="drag"
        >
          <template #item="{ element: field, index }">
            <v-expansion-panel :key="field.key">
              <template #title>
                <div class="d-flex w-100 align-center">
                  <v-icon
                    v-if="!sortableOptions.disabled"
                    :icon="mdiDragHorizontal"
                    class="mr-2 handle cursor-move"
                  />

                  <v-checkbox
                    :disabled="mode === 'details'"
                    class="mr-2"
                    hideDetails
                    :modelValue="field.value.combination?.isEnabled"
                    @click.stop="toggleActivation(index)"
                  />

                  <TaskStepTitle :combination="getTitle(field.value.combination)" />

                  <div v-if="task.taskType === 'BeamConstancyCheck'" class="ms-auto d-inline-flex">
                    <Notification
                      :value="
                        getNotification(
                          field.value.combination?.referenceMeasurementInfo
                            ?.referenceMeasurementState,
                        )
                      "
                    />
                  </div>
                </div>
              </template>

              <template #text>
                <v-row>
                  <v-col v-if="field.value.combination?.targetSystemValues?.length" cols="6">
                    <ParameterTable
                      :title="$t('TargetSystemParameter_other')"
                      :parameters="field.value.combination?.targetSystemValues || undefined"
                    />
                  </v-col>

                  <v-col v-if="field.value.combination?.supportSystemValues?.length" cols="6">
                    <ParameterTable
                      :title="$t('SupportSystemParameter_other')"
                      :parameters="field.value.combination?.supportSystemValues || undefined"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col v-if="task.taskType === 'ManualTask'" cols="12">
                    <JobConfigurationManualEvaluations
                      :evaluations="field.value.evaluations || undefined"
                    />
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel>
          </template>
        </Sortable>
      </v-expansion-panels>
    </v-col>
  </v-row>
</template>
